/**
 * PRReviewPanel component.
 *
 * Panel for "PR Review" attention items (pr_review).
 * Shows the PR context, diff summary, and provides review actions.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { GitPullRequest, ExternalLink, Check, X } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'
import type { AttentionItem } from '../types'
import { getAttentionTypeBadgeColor } from '../types'

export interface PRReviewPanelProps {
  /** The attention item being reviewed. */
  item: AttentionItem
  /** Whether a resolution action is pending. */
  actionPending?: boolean
  /** Called when the operator approves the PR. */
  onApprove?: () => void
  /** Called when the operator requests changes. */
  onRequestChanges?: (feedback: string) => void
  /** Called when the operator dismisses the item. */
  onDismiss?: () => void
}

/**
 * PR review panel for engineering PR attention items.
 */
export function PRReviewPanel({
  item,
  actionPending,
  onApprove,
  onRequestChanges,
  onDismiss,
}: PRReviewPanelProps) {
  const [feedback, setFeedback] = React.useState('')
  const badgeColor = getAttentionTypeBadgeColor(item.attention_type)

  // Extract PR-specific details
  const prUrl = item.details?.['pr_url'] as string | undefined
  const prNumber = item.details?.['pr_number'] as string | number | undefined
  const repoName = item.details?.['repo_name'] as string | undefined
  const author = item.details?.['author'] as string | undefined
  const changedFiles = item.details?.['changed_files'] as number | undefined
  const additions = item.details?.['additions'] as number | undefined
  const deletions = item.details?.['deletions'] as number | undefined

  return (
    <div style={styles.container}>
      <Card>
        <CardHeader style={styles.header}>
          <div style={styles.headerRow}>
            <Badge
              variant="outline"
              style={{ borderColor: badgeColor, color: badgeColor }}
            >
              PR Review
            </Badge>
            <GitPullRequest style={styles.prIcon} />
            <CardTitle style={styles.title}>{item.title}</CardTitle>
          </div>
          <div style={styles.metaRow}>
            {repoName && <span style={styles.repo}>{repoName}</span>}
            {prNumber && <span style={styles.prNumber}>#{prNumber}</span>}
            {author && (
              <span style={styles.author}>by {author}</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Subtitle / description */}
          <p style={styles.subtitle}>{item.subtitle}</p>

          {/* Stats row */}
          {(changedFiles !== undefined || additions !== undefined || deletions !== undefined) && (
            <div style={styles.statsRow}>
              {changedFiles !== undefined && (
                <span style={styles.stat}>{changedFiles} files changed</span>
              )}
              {additions !== undefined && (
                <span style={styles.additions}>+{additions}</span>
              )}
              {deletions !== undefined && (
                <span style={styles.deletions}>-{deletions}</span>
              )}
            </div>
          )}

          {/* PR URL link */}
          {prUrl && (
            <a
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.prLink}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink style={styles.linkIcon} />
              View on GitHub
            </a>
          )}

          {/* Feedback area */}
          <div style={styles.feedbackSection}>
            <textarea
              style={styles.textarea}
              placeholder="Leave a comment (optional for approve, required for changes)..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
            />
          </div>

          {/* Action buttons */}
          <div style={styles.actions}>
            <Button
              variant="default"
              disabled={actionPending}
              onClick={onApprove}
              style={styles.approveBtn}
            >
              <Check style={styles.actionIcon} />
              {actionPending ? 'Processing...' : 'Approve'}
            </Button>
            <Button
              variant="destructive"
              disabled={!feedback.trim() || actionPending}
              onClick={() => onRequestChanges?.(feedback.trim())}
            >
              <X style={styles.actionIcon} />
              Request Changes
            </Button>
            <Button
              variant="ghost"
              disabled={actionPending}
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  prIcon: {
    width: '18px',
    height: '18px',
    color: 'var(--category-communication)',
    flexShrink: 0,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  repo: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    padding: '1px 6px',
    borderRadius: '4px',
    background: 'var(--bg-tertiary)',
  },
  prNumber: {
    fontWeight: 500,
  },
  author: {
    // default
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    marginBottom: '12px',
    lineHeight: 1.5,
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    fontSize: '12px',
  },
  stat: {
    color: 'var(--text-muted)',
  },
  additions: {
    color: 'var(--success)',
    fontWeight: 600,
  },
  deletions: {
    color: 'var(--error)',
    fontWeight: 600,
  },
  prLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'var(--accent-primary)',
    textDecoration: 'none',
    marginBottom: '16px',
  },
  linkIcon: {
    width: '14px',
    height: '14px',
  },
  feedbackSection: {
    marginBottom: '16px',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    lineHeight: 1.5,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    outline: 'none',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  approveBtn: {
    background: 'var(--success)',
    borderColor: 'var(--success)',
  },
  actionIcon: {
    width: '14px',
    height: '14px',
    marginRight: '6px',
  },
}

export default PRReviewPanel
